/**
 * Password utilities
 */

const UPPER = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
const LOWER = 'abcdefghijklmnopqrstuvwxyz';
const DIGITS = '0123456789';
const SPECIAL = '!@#$%^&*';
const CHARSET = LOWER + UPPER + DIGITS + SPECIAL;
const LENGTH = 16;

// bcrypt stops at 72 bytes: counted in bytes, never in characters ("é" costs two).
export const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_MAX_BYTES = 72;
const passwordBytes = (value) => new TextEncoder().encode(value).length;

// Same classes as the backend rule (`user.schema.ts`): special = neither letter, digit nor whitespace.
/** @type {ReadonlyArray<{ key: 'length' | 'upper' | 'lower' | 'digit' | 'special', test: (value: string) => boolean }>} */
export const PASSWORD_CRITERIA = [
  { key: 'length', test: (value) => value.length >= PASSWORD_MIN_LENGTH },
  { key: 'upper', test: (value) => /\p{Lu}/u.test(value) },
  { key: 'lower', test: (value) => /\p{Ll}/u.test(value) },
  { key: 'digit', test: (value) => /\p{Nd}/u.test(value) },
  { key: 'special', test: (value) => /[^\p{L}\p{N}\s]/u.test(value) },
];

/**
 * Why a password fails the rule, in the backend's order (`passwordIssue` in `user.schema.ts`).
 * @type {(value: string) => 'too_short' | 'too_long' | 'weak' | null}
 */
export const passwordIssue = (value) => {
  if (value.length < PASSWORD_MIN_LENGTH) return 'too_short';
  if (passwordBytes(value) > PASSWORD_MAX_BYTES) return 'too_long';
  return PASSWORD_CRITERIA.every(({ test }) => test(value)) ? null : 'weak';
};

/** Uniform integer in [0, max), max ≤ 256: bytes past the last whole multiple are rejected. */
const randomBelow = (max) => {
  const limit = 256 - (256 % max);
  const byte = new Uint8Array(1);
  do crypto.getRandomValues(byte);
  while (byte[0] >= limit);
  return byte[0] % max;
};

const pick = (chars) => chars[randomBelow(chars.length)];

/**
 * Generate a secure random password
 * @returns {string} 16-character password, one character of each class at least
 */
export const generateRandomPassword = () => {
  const chars = [pick(UPPER), pick(LOWER), pick(DIGITS), pick(SPECIAL)];
  while (chars.length < LENGTH) chars.push(pick(CHARSET));
  // Fisher–Yates, so the forced characters do not stay in front.
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = randomBelow(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }
  return chars.join('');
};
